import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Usb, Unplug, FolderOpen } from 'lucide-react';
import { getUSBEvents } from '../services/api';
import { useScrollToEvent } from '../hooks/useScrollToEvent';
import dayjs from 'dayjs';
import { PageHeader } from '../components/ui/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { EmptyState } from '../components/ui/empty-state';

function USBActivityPage() {
  const { workerId } = useParams();
  const [usbEvents, setUsbEvents] = useState([]);
  useScrollToEvent(usbEvents);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUSBEvents();
    const interval = setInterval(loadUSBEvents, 30000);
    return () => clearInterval(interval);
  }, [workerId]);

  const loadUSBEvents = async () => {
    try {
      setLoading(true);
      const response = await getUSBEvents(50, workerId);
      setUsbEvents(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load USB events:', err);
      setError('Failed to load USB events');
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'connected':
        return <Usb className="h-5 w-5 text-success" />;
      case 'disconnected':
        return <Unplug className="h-5 w-5 text-destructive" />;
      case 'file_transfer':
        return <FolderOpen className="h-5 w-5 text-warning" />;
      default:
        return <Usb className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getEventVariant = (eventType) => {
    switch (eventType) {
      case 'connected':
        return 'success';
      case 'disconnected':
        return 'outline';
      case 'file_transfer':
        return 'warning';
      default:
        return 'outline';
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader title="USB Activity" description="Monitor USB device connections and file transfers" />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">USB Events</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading...</div>
          ) : usbEvents.length === 0 ? (
            <EmptyState
              icon={Usb}
              title="No USB events"
              description="No USB events recorded yet. Device connections and file transfers will appear here."
            />
          ) : (
            <ul className="divide-y divide-border">
              {usbEvents.map((event, index) => (
                <li
                  key={event.id ?? index}
                  id={`event-${event.id}`}
                  className="flex items-start gap-3 p-4"
                >
                  <div className="pt-0.5">{getEventIcon(event.event_type)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{event.device_name}</span>
                      <Badge variant={getEventVariant(event.event_type)}>{event.event_type}</Badge>
                      {event.risk_score > 0 && (
                        <Badge variant="destructive">Risk: {event.risk_score}</Badge>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {dayjs(event.timestamp).format('MMM DD, YYYY HH:mm:ss')}
                    </div>
                    {event.files_transferred > 0 && (
                      <div className="text-xs text-muted-foreground">
                        Files: {event.files_transferred} • Size: {formatBytes(event.total_size)}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default USBActivityPage;
